import { FilterList } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { FacetMultiSelectProps, FacetOption } from "./types";

export const FacetMultiSelect = <TData extends object>({
  column,
  filterValue,
}: FacetMultiSelectProps<TData>): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();

  const facetedUniqueValues = column.getFacetedUniqueValues();

  const options = React.useMemo<FacetOption[]>(() => {
    if (facetedUniqueValues.size === 0) return [];

    return Array.from(facetedUniqueValues.entries())
      .sort((a, b) => {
        const [valueA] = a as [unknown, number];
        const [valueB] = b as [unknown, number];

        return String(valueA).localeCompare(String(valueB));
      })
      .map((entry) => {
        const [value, count] = entry as [unknown, number];

        return {
          label: String(value),
          value: String(value),
          count: Number(count),
        };
      });
  }, [facetedUniqueValues]);

  const selectedValues = Array.isArray(filterValue)
    ? (filterValue as string[])
    : [];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleOption = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];

    column.setFilterValue(newValues.length > 0 ? newValues : undefined);
  };

  const handleToggleAll = () => {
    const allValues = options.map((option) => option.value);
    const isAllValuesSelected =
      allValues.length > 0 &&
      allValues.every((value) => selectedValues.includes(value));

    if (isAllValuesSelected) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue(allValues);
    }
  };

  const isAllSelected =
    options.length > 0 &&
    options.every((option) => selectedValues.includes(option.value));

  const columnName = String(column.columnDef.header);
  const selectedCount = selectedValues.length;
  const buttonText =
    selectedCount > 0 ? `${columnName} (${selectedCount})` : columnName;

  return (
    <>
      <Button
        endIcon={<FilterList sx={{ height: 16, width: 16 }} />}
        onClick={handleClick}
        size="small"
        sx={{
          color: theme.colors.text[1],
          fontWeight: selectedCount > 0 ? 600 : "inherit",
          borderColor: "divider",
          fontSize: 12,
          padding: 0,
          justifyContent: "flex-start",
          "&:hover": {
            backgroundColor: "inherit",
          },
        }}
        variant="text"
      >
        {buttonText}
      </Button>
      <Menu
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 304,
              maxWidth: 200,
              minWidth: 150,
            },
          },
        }}
      >
        {options.length > 0 && (
          <MenuItem
            dense
            onClick={handleToggleAll}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              height: 20,
              paddingX: 0.5,
            }}
          >
            <Checkbox
              checked={isAllSelected}
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: 20,
                  color: theme.colors.primary[5],
                },
              }}
            />
            <Typography
              color={theme.colors.text[6]}
              fontSize={12}
              fontWeight={500}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              All
            </Typography>
          </MenuItem>
        )}
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);

          return (
            <MenuItem
              dense
              key={option.value}
              onClick={() => handleToggleOption(option.value)}
              sx={{ height: 20, paddingX: 0.5 }}
            >
              <Checkbox
                checked={isSelected}
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 20,
                    color: theme.colors.primary[5],
                  },
                }}
              />
              <Typography
                color={theme.colors.text[6]}
                fontSize={12}
                fontWeight={500}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={option.label}
              >
                {option.label} ({option.count})
              </Typography>
            </MenuItem>
          );
        })}
        {options.length === 0 && (
          <MenuItem disabled>
            <Typography color="text.secondary" variant="body2">
              No options available
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
